import { Injectable, InjectionToken, inject } from '@angular/core';
import { NavigationError } from '@angular/router';

interface RecoveryBrowser {
  readonly origin: string;
  readonly online: boolean;
  readonly storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload(url: string): void;
}

export const LAZY_ROUTE_BROWSER = new InjectionToken<RecoveryBrowser>('Lazy route recovery browser', {
  providedIn: 'root', factory: () => ({
    origin: window.location.origin,
    get online() { return navigator.onLine; },
    get storage() { return window.sessionStorage; },
    reload: (url: string) => window.location.assign(url)
  })
});

const ATTEMPT_KEY = 'WITA_LAZY_ROUTE_RECOVERY';
const RETRY_WINDOW_MS = 60_000;
const MODULE_LOAD_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk .+ failed|ChunkLoadError/i;

/** Recover stale lazy-route assets after deployment, without clearing authentication. */
@Injectable({ providedIn: 'root' })
export class LazyRouteRecoveryService {
  private readonly browser = inject(LAZY_ROUTE_BROWSER);

  recover(event: Pick<NavigationError, 'url' | 'error'>): boolean {
    if (!(event.error instanceof Error) || !MODULE_LOAD_ERROR.test(event.error.message) || !this.browser.online) return false;
    try {
      const target = new URL(event.url, this.browser.origin);
      if (target.origin !== this.browser.origin) return false;
      const now = Date.now();
      const previous = this.browser.storage.getItem(ATTEMPT_KEY);
      let attemptedAt: unknown;
      try { attemptedAt = previous ? JSON.parse(previous).at : undefined; } catch { /* Ignore malformed retry state. */ }
      if (typeof attemptedAt === 'number' && now - attemptedAt >= 0 && now - attemptedAt < RETRY_WINDOW_MS) return false;
      // Write before reloading: an unavailable new asset must not create a reload loop.
      this.browser.storage.setItem(ATTEMPT_KEY, JSON.stringify({ at: now }));
      this.browser.reload(target.pathname + target.search + target.hash);
      return true;
    } catch {
      // Without reliable retry storage, avoid automatic reloads altogether.
      return false;
    }
  }
}
